import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { api, errorMessage, fmtDate, getApiKey, setApiKey } from "@/lib/api";
import { DEFAULT_PREFS, buildJobOptions, getPrefs, setPrefs } from "@/lib/prefs";
import { useSeo } from "@/lib/seo";
import { KeyRound, Plus, Ban, Copy, SlidersHorizontal, ChevronDown, RotateCcw, Webhook } from "lucide-react";

const LANG_LABEL = { "nb-NO": "Norsk (bokmål)", "en-GB": "English (UK)", "en-US": "English (US)" };
const AUTO = "__auto__";

export default function SettingsPage() {
  useSeo({ title: "Lab · Innstillinger", noindex: true });
  const [config, setConfig] = useState(null);
  const [keys, setKeys] = useState([]);
  const [envKeys, setEnvKeys] = useState(0);
  const [label, setLabel] = useState("digihome-backend");
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const [prefs, setLocalPrefs] = useState(getPrefs());
  const [engineOpen, setEngineOpen] = useState(false);

  const load = () => {
    api.get("/lab/config").then((r) => setConfig(r.data)).catch((e) => toast.error(errorMessage(e)));
    api.get("/lab/api-keys").then((r) => { setKeys(r.data.items); setEnvKeys(r.data.env_keys); }).catch(() => {});
  };
  useEffect(load, []);

  const update = (patch) => {
    const next = { ...prefs, ...patch };
    setLocalPrefs(next);
    setPrefs(patch);
  };
  const resetPrefs = () => { setLocalPrefs({ ...DEFAULT_PREFS }); localStorage.removeItem("listing_ai_prefs"); toast.success("Standardvalg tilbakestilt"); };

  const create = async () => {
    setBusy(true);
    try {
      const r = await api.post("/lab/api-keys", { label });
      setCreated(r.data.key);
      toast.success("Nøkkel opprettet – kopier den nå");
      load();
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const revoke = async (id) => {
    try { await api.post(`/lab/api-keys/${id}/revoke`); toast.success("Nøkkel trukket tilbake"); load(); } catch (e) { toast.error(errorMessage(e)); }
  };

  const effective = config ? buildJobOptions(prefs, config) : null;
  const versions = config?.pipeline_versions || [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6 max-w-[1100px]">
      <div><h1 className="text-2xl font-semibold tracking-tight">Innstillinger</h1><p className="text-sm text-muted-foreground">Standardvalg for nye jobber, tjenestenøkler og motorkonfigurasjon.</p></div>

      {/* ------------------------------------------------------------ run defaults */}
      <Card data-testid="settings-run-defaults">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" />Standardvalg for nye jobber</CardTitle>
          <CardDescription>Brukes automatisk hver gang du starter en jobb fra «Ny annonse». Lagres i denne nettleseren.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Bildemotor</Label>
              <Select value={prefs.pipeline_version || AUTO} onValueChange={(v) => update({ pipeline_version: v === AUTO ? null : v })}>
                <SelectTrigger data-testid="pref-version-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Anbefalt ({config?.default_pipeline_version || "…"})</SelectItem>
                  {versions.map((v) => <SelectItem key={v.version} value={v.version}>{v.version}{v.stable ? "" : " (eksperimentell)"}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">{versions.find((v) => v.version === effective?.version)?.description}</p>
            </div>
            <div className="space-y-1.5">
              <Label>Språk for annonseutkast</Label>
              <Select value={prefs.language || "nb-NO"} onValueChange={(v) => update({ language: v })}>
                <SelectTrigger data-testid="pref-language-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(config?.languages || ["nb-NO"]).map((l) => <SelectItem key={l} value={l}>{LANG_LABEL[l] || l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div><Label>Generativ forbedring</Label><p className="text-xs text-muted-foreground mt-0.5">Bruker en bildemodell i tillegg til de vanlige justeringene. Alt som kommer ut kontrolleres mot originalen før det leveres.</p></div>
              <Switch checked={Boolean(effective?.gen)} onCheckedChange={(v) => update({ enable_generative_enhancement: v })} data-testid="pref-generative-switch" />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div><Label>Streng kvalitetskontroll</Label><p className="text-xs text-muted-foreground mt-0.5">Anbefalt. Bilder der noe i boligen kan være endret leveres aldri som optimalisert.</p></div>
              <Switch checked={effective ? effective.strict : true} onCheckedChange={(v) => update({ strict_fidelity: v })} data-testid="pref-strict-switch" />
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
            <div><Label>Virtuell møblering – «La DigiHome vurdere»</Label><p className="text-xs text-muted-foreground mt-0.5">For langtidsleie: tomme stuer, soverom, spiseplasser og kontorer får et forslag til virtuell møblering. Alltid merket, aldri hovedbilde, aldri brukt før noen sier ja.</p></div>
            <Switch checked={prefs.virtual_staging !== false} onCheckedChange={(v) => update({ virtual_staging: v })} data-testid="pref-staging-switch" />
          </div>

          <div className="rounded-lg border p-3 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div><Label className="flex items-center gap-1.5"><Webhook className="h-3.5 w-3.5" />Varsle når jobben er ferdig (webhook)</Label><p className="text-xs text-muted-foreground mt-0.5">Sender en signert melding til DigiHome-backend når jobben er ferdig. Uten egen adresse brukes den innebygde testmottakeren.</p></div>
              <Switch checked={prefs.webhook_enabled} onCheckedChange={(v) => update({ webhook_enabled: v })} data-testid="pref-webhook-switch" />
            </div>
            {prefs.webhook_enabled && (
              <Input value={prefs.webhook_url} onChange={(e) => update({ webhook_url: e.target.value })} placeholder={config?.webhook_sink_url || "https://…"} className="font-mono text-xs h-9" data-testid="pref-webhook-url" />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
            <span data-testid="pref-effective-summary">Gjelder nå: <span className="font-mono">{effective?.version || "…"}</span>{effective?.gen ? " · generativ" : " · ikke-generativ"}{effective?.strict ? " · streng kontroll" : " · lempelig kontroll"}{effective?.staging ? " · virtuell møblering: auto" : " · virtuell møblering: av"} · {LANG_LABEL[effective?.language] || effective?.language}</span>
            <Button variant="ghost" size="sm" className="ml-auto" onClick={resetPrefs} data-testid="pref-reset"><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Tilbakestill</Button>
          </div>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------ keys */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="h-4 w-4" />Tjenestenøkler</CardTitle><CardDescription>{envKeys} nøkkel/nøkler fra miljøet + roterbare nøkler under. Tilbaketrekking gjelder umiddelbart.</CardDescription></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1.5"><Label htmlFor="label">Navn</Label><Input id="label" value={label} onChange={(e) => setLabel(e.target.value)} className="w-56" data-testid="apikey-label-input" /></div>
            <Button onClick={create} disabled={busy} data-testid="apikey-create-button"><Plus className="h-4 w-4 mr-1.5" />Lag nøkkel</Button>
          </div>
          {created && (
            <div className="rounded-lg verdict-pass border p-3 text-sm flex flex-wrap items-center gap-2" data-testid="apikey-created">
              <span className="font-mono break-all">{created}</span>
              <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(created); toast.success("Kopiert"); }}><Copy className="h-3.5 w-3.5 mr-1" />Kopier</Button>
              <span className="text-xs">Vises bare én gang. Lagre den i DigiHome-backend sine secrets.</span>
            </div>
          )}
          <div className="divide-y" data-testid="apikey-list">
            {keys.length === 0 && <div className="text-sm text-muted-foreground py-2">Ingen roterbare nøkler ennå.</div>}
            {keys.map((k) => (
              <div key={k.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="font-mono text-xs">{k.prefix}…</span><span>{k.label}</span>
                <span className={`rounded px-1.5 text-[10px] border ${k.active ? "verdict-pass" : "verdict-reject"}`}>{k.active ? "aktiv" : "trukket"}</span>
                <span className="text-xs text-muted-foreground">{fmtDate(k.created_at)}</span>
                {k.active && <Button size="sm" variant="ghost" className="ml-auto" onClick={() => revoke(k.id)} data-testid={`apikey-revoke-${k.id}`}><Ban className="h-3.5 w-3.5 mr-1" />Trekk tilbake</Button>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Denne nettleseren</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3 text-sm">
          <span>Bruker nøkkel <span className="font-mono">{getApiKey().slice(0, 12)}…</span></span>
          <Button variant="outline" size="sm" onClick={() => { setApiKey(""); window.location.reload(); }} data-testid="settings-change-key">Bytt nøkkel</Button>
        </CardContent>
      </Card>

      {/* ------------------------------------------------------------ engine (collapsed) */}
      <Collapsible open={engineOpen} onOpenChange={setEngineOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <button type="button" className="w-full text-left" data-testid="settings-engine-toggle">
              <CardHeader className="pb-3 flex-row items-center justify-between space-y-0">
                <div><CardTitle className="text-base">Motor og versjoner</CardTitle><CardDescription>Teknisk konfigurasjon – styres av miljøet, ikke av Lab.</CardDescription></div>
                <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${engineOpen ? "rotate-180" : ""}`} />
              </CardHeader>
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="grid gap-6 md:grid-cols-2">
              <div className="text-sm space-y-2" data-testid="settings-engine">
                <Row k="Vision (leverandør/modell)" v={config?.models?.vision} />
                <Row k="Bildemodell (leverandør/modell)" v={config?.models?.enhancer} />
                <Row k="Møbleringsmodell (leverandør/modell)" v={config?.models?.stager} />
                <Row k="Standard bildemotor" v={config?.default_pipeline_version} />
                <Row k="Språk" v={(config?.languages || []).join(", ")} />
                <Row k="Maks bilder per jobb" v={config?.limits?.max_images_per_job} />
                <Row k="Maks filstørrelse" v={config?.limits ? `${Math.round(config.limits.max_image_bytes / 1048576)} MB` : "–"} />
                <Row k="Tillatte filtyper" v={(config?.limits?.allowed_mime || []).join(", ")} />
                <Row k="Offentlig base-URL" v={config?.public_base_url} />
                <Row k="Importkilder" v={(config?.import_sources || []).map((s) => `${s.label} (${s.status})`).join(", ")} />
              </div>
              <div className="space-y-3" data-testid="settings-versions">
                {versions.map((v) => (
                  <div key={v.version} className="rounded-lg border p-3 text-sm">
                    <div className="flex items-center gap-2"><span className="font-mono font-semibold">{v.version}</span>{v.stable ? <span className="rounded verdict-pass border px-1.5 text-[10px]">stabil</span> : <span className="rounded verdict-needs_review border px-1.5 text-[10px]">eksperimentell</span>}{v.version === config?.default_pipeline_version && <span className="rounded bg-secondary px-1.5 text-[10px]">standard</span>}</div>
                    <div className="text-xs text-muted-foreground mt-1">{v.description}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-mono">
                      <span className="rounded bg-secondary px-1.5 py-0.5">generative={String(v.enable_generative_enhancement)}</span>
                      <span className="rounded bg-secondary px-1.5 py-0.5">strict={String(v.strict_fidelity)}</span>
                      {Object.entries(v.thresholds || {}).map(([k, val]) => <span key={k} className="rounded bg-secondary px-1.5 py-0.5">{k}={val}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
}

const Row = ({ k, v }) => <div className="flex justify-between gap-4 border-b last:border-0 py-1.5"><span className="text-muted-foreground">{k}</span><span className="font-mono text-xs text-right break-all">{v ?? "–"}</span></div>;
